import { DocumentText } from "@medusajs/icons"
import { Button, Skeleton, StatusBadge, Text } from "@medusajs/ui"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { DeliveryBadge, PaymentBadge } from "../components/order-badges"
import { PageHeader } from "../components/page"
import { Empty, Ref, SearchInput, Tabs } from "../components/ui"
import { bookingApi, dayLabel, type OrderBooking } from "../lib/booking"
import { ago, money, QUEUE_HINT, QUEUE_LABEL } from "../lib/format"
import { t, tc, tk } from "../lib/i18n"
import { fold } from "../lib/custom-filters"
import { errorMessage, hqApi, type OrderListItem, type Queue } from "../lib/medusa"
import { slipsUrl } from "../lib/slip"

const TABS: Array<Queue | "all"> = ["to_ship", "to_arrange", "cash_to_collect", "done", "canceled", "all"]

const EMPTY: Record<Queue | "all", { title: string; hint: string }> = {
  to_ship: { title: tk("Nothing to ship"), hint: tk("Every BOX NOW order has its voucher.") },
  to_arrange: { title: tk("Nothing to arrange"), hint: tk("Every meet-up and home delivery has been handed over.") },
  cash_to_collect: { title: tk("No cash to collect"), hint: tk("Every cash payment is recorded.") },
  done: { title: tk("No finished orders yet"), hint: "" },
  canceled: { title: tk("No cancelled orders"), hint: "" },
  all: { title: tk("No orders yet"), hint: "" },
}

export function OrdersPage() {
  const [params, setParams] = useSearchParams()
  const queue = (params.get("queue") as Queue | "all" | null) ?? "to_ship"
  // The queue and the search live in the address, so Back from an order comes back to the same list.
  const search = params.get("q") ?? ""
  const update = (patch: { queue?: Queue | "all"; q?: string }) => {
    const next = new URLSearchParams(params)
    if (patch.queue !== undefined) {
      if (patch.queue === "to_ship") next.delete("queue")
      else next.set("queue", patch.queue)
    }
    if (patch.q !== undefined) {
      if (patch.q) next.set("q", patch.q)
      else next.delete("q")
    }
    setParams(next, { replace: true })
  }
  const setSearch = (q: string) => update({ q })
  const orders = useQuery({ queryKey: ["hq", "orders"], queryFn: hqApi.orders, refetchInterval: 60_000 })

  // The search works inside the chosen queue; each queue's count is what it holds of the matches, so
  // a search shows at once where its order is (and "All" searches everything).
  const matching = useMemo(() => {
    const all = orders.data?.orders ?? []
    const raw = fold(search.trim())
    const term = /^(#|w-?)\d/.test(raw) ? raw.replace(/^(#|w-?)/, "") : raw.replace(/^@/, "")
    if (!term) return all
    return all.filter((order) =>
      [order.ref, String(order.display_id ?? ""), order.customer, order.instagram ?? "", order.tracking ?? "", order.first_item ?? ""].some((field) => fold(field).includes(term))
    )
  }, [orders.data, search])
  const inQueue = (tab: Queue | "all") => (tab === "all" ? matching : matching.filter((order) => order.queues.includes(tab)))
  const visible = useMemo(() => inQueue(queue), [matching, queue])
  const elsewhere = search && queue !== "all" ? matching.length - visible.length : 0

  // Whether each meet-up / home-delivery order on screen has a delivery booking (the booking app).
  // A failure (booking system not connected) shows nothing rather than "not booked".
  const bookable = visible.filter((order) => (order.delivery === "meetup" || order.delivery === "home") && order.status !== "canceled" && !order.delivered).map((order) => order.id).slice(0, 100)
  const bookings = useQuery({
    queryKey: ["booking", "orders", bookable.join(",")],
    queryFn: () => bookingApi.orders(bookable),
    enabled: bookable.length > 0,
    retry: false,
    staleTime: 60_000,
  })

  return (
    <div className="flex flex-col gap-y-4">
      <PageHeader title={t("Orders")} subtitle={t("Orders from highclub.store. Open one to ship it, hand it over or record the cash.")} />

      <SearchInput value={search} onChange={setSearch} placeholder={t("W-46, name, Instagram, voucher")} label={t("Search orders by number, name, Instagram, voucher or item")} />

      <Tabs
        label={t("Order queues")}
        value={queue}
        onChange={(tab) => update({ queue: tab })}
        items={TABS.map((tab) => ({
          key: tab,
          label: tab === "all" ? tc("orders", "All") : QUEUE_LABEL[tab],
          count: orders.data ? (search ? inQueue(tab).length : tab === "all" ? orders.data.orders.length : orders.data.counts[tab]) : null,
        }))}
      />

      {!search && (
        <div className="flex min-h-[32px] items-center justify-between gap-x-3">
          <Text size="small" className="text-ui-fg-muted">
            {queue === "all" ? t("The {n} most recent orders.", { n: orders.data?.orders.length ?? "" }) : QUEUE_HINT[queue]}
          </Text>
          {(queue === "to_ship" || queue === "to_arrange") && visible.length > 0 && (
            <Button variant="secondary" size="small" className="shrink-0" asChild>
              <Link to={slipsUrl(visible.map((order) => order.id))}>
                <DocumentText /> {t("Packing slips ({n})", { n: visible.length })}
              </Link>
            </Button>
          )}
        </div>
      )}
      {search && orders.data && (
        <Text size="small" className="text-ui-fg-muted">
          {elsewhere > 0
            ? t.rich("{n} here, <b>{more} more in other lists</b>.", { b: (text: string) => <button type="button" className="text-ui-fg-interactive" onClick={() => update({ queue: "all" })}>{text}</button> }, { n: String(visible.length), more: String(elsewhere) })
            : visible.length
              ? t.n(visible.length, "{n} order matches.", "{n} orders match.")
              : ""}
        </Text>
      )}

      {orders.isLoading && (
        <div className="flex flex-col gap-y-2">
          {[0, 1, 2].map((key) => <Skeleton key={key} className="h-[88px] w-full rounded-lg" />)}
        </div>
      )}
      {orders.isError && <Text className="text-ui-fg-error">{errorMessage(orders.error)}</Text>}
      {orders.data && visible.length === 0 && (
        search ? (
          <Empty title={t("No order matches that")}>{t("Try the number without W-, part of the name, or the Instagram handle.")}</Empty>
        ) : (
          <Empty title={t(EMPTY[queue].title)}>{EMPTY[queue].hint && t(EMPTY[queue].hint)}</Empty>
        )
      )}

      <ul className="flex flex-col gap-y-2">
        {visible.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            booking={bookings.data && bookable.includes(order.id) ? (bookings.data.bookings[order.id] ?? null) : undefined}
          />
        ))}
      </ul>
    </div>
  )
}

/** undefined = not asked (or unknown); null = asked, and not booked. */
function OrderCard({ order, booking }: { order: OrderListItem; booking?: OrderBooking | null }) {
  return (
    <li>
      <Link
        to={`/orders/${order.id}`}
        className="bg-ui-bg-base shadow-elevation-card-rest hover:shadow-elevation-card-hover active:bg-ui-bg-base-pressed flex gap-x-3 rounded-lg p-3 transition-shadow"
      >
        <div className="bg-ui-bg-subtle size-14 shrink-0 overflow-hidden rounded-md">
          {order.thumbnail && <img src={order.thumbnail} alt="" loading="lazy" className="size-full object-cover" />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-y-1.5">
          <div className="flex items-center justify-between gap-x-2">
            <div className="flex min-w-0 items-center gap-x-2">
              <Ref>{order.ref}</Ref>
              <span className="txt-compact-small-plus truncate">{order.customer}</span>
            </div>
            <span className="hc-data shrink-0 text-[13px]">{money(order.total)}</span>
          </div>
          <Text size="small" className="text-ui-fg-subtle -mt-0.5 truncate">
            {order.first_item}
            {order.units > 1 ? ` ${t("+ {n} more", { n: order.units - 1 })}` : ""}
          </Text>
          <div className="flex flex-wrap items-center gap-1.5">
            <DeliveryBadge delivery={order.delivery} />
            <PaymentBadge payment={order.payment} />
            {order.exchange_to_send && <StatusBadge color="orange">{t("Exchange to send")}</StatusBadge>}
            {booking === null && <StatusBadge color="orange">{t("Not booked")}</StatusBadge>}
            {booking && <StatusBadge color="green">{t("Booked {when}", { when: booking.time ? `${dayLabel(booking.date)} ${booking.time}` : dayLabel(booking.date) })}</StatusBadge>}
            <Text size="xsmall" className="text-ui-fg-muted ml-auto">
              {ago(order.created_at)}
            </Text>
          </div>
        </div>
      </Link>
    </li>
  )
}
